import {
  AppleOutlined,
  DesktopOutlined,
  DownloadOutlined,
  WindowsOutlined,
} from '@ant-design/icons';
import { OS_ARCHITECTURES, OS_OPTIONS } from '@app/applicationVersions/components/options';
import { ApplicationVersionDto } from '@app/applicationVersions/dto';
import { useDownloadApp } from '@hooks/use-download-app';
import { useLatestAppVersions } from '@hooks/use-fetch-latest-app-versions';
import { useTranslations } from '@i18n/use-translations';
import { Button, Dropdown, type MenuProps } from 'antd';
import { useCallback } from 'react';

const OS_ICONS: Record<string, React.ReactNode> = {
  Windows: <WindowsOutlined />,
  macOs: <AppleOutlined />,
  Linux: <DesktopOutlined />,
};

export const DownloadAppButton: React.FC = () => {
  const t = useTranslations();

  const { Linux, Windows, macOs, isFetching } = useLatestAppVersions();
  const { downloadApp } = useDownloadApp();

  const handleDownload = useCallback(
    async (os: string, cpuArchitecture: string) => {
      //make a switch case to find the correct version based on os and cpuArchitecture
      let version: ApplicationVersionDto | undefined;
      switch (os) {
        case 'Windows':
          version = cpuArchitecture === 'x64' ? Windows.x64 : Windows.arm64;
          break;
        case 'macOs':
          version = cpuArchitecture === 'x64' ? macOs.x64 : macOs.arm64;
          break;
        case 'Linux':
          version = cpuArchitecture === 'x64' ? Linux.x64 : Linux.arm64;
          break;
      }
      if (version) {
        downloadApp(version);
      } else {
        t('applicationVersions.download.notFound');
      }
    },
    [Windows.arm64, Windows.x64, t],
  );

  const items: MenuProps['items'] = OS_OPTIONS.map(({ label, value }) => ({
    key: value,
    icon: OS_ICONS[value],
    label,
    children: (OS_ARCHITECTURES[value] ?? []).map(arch => {
      return {
        key: `${value}-${arch}`,
        label: t(`applicationVersions.download.${arch}`),
        onClick: () => handleDownload(value, arch),
        disabled:
          (value === 'Windows' && !Windows[arch as keyof typeof Windows]) ||
          (value === 'macOs' && !macOs[arch as keyof typeof macOs]) ||
          (value === 'Linux' && !Linux[arch as keyof typeof Linux]),
      };
    }),
  }));

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button icon={<DownloadOutlined />} loading={isFetching}>
        {t('applicationVersions.download.buttonLabel')}
      </Button>
    </Dropdown>
  );
};
